import { useEffect, useRef } from 'react';

interface MouseTrailOptions {
  colors: string[];
  size: number;
  opacity: number;
  trailLength: number;
  enabled: boolean;
}

export const useMouseTrail = (options: MouseTrailOptions) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const mousePositions = useRef<{ x: number; y: number; time: number }[]>([]);
  const animationFrameId = useRef<number>();

  useEffect(() => {
    if (!options.enabled) return;
    
    // Prevent duplicate canvas creation
    if (document.body.dataset.mouseTrail === 'active') {
      console.info('[MouseTrail] Already active, skipping initialization');
      return;
    }
    
    console.info('[MouseTrail] Creating canvas');
    document.body.dataset.mouseTrail = 'active';

    // Create canvas element
    const canvas = document.createElement('canvas');
    canvas.style.position = 'fixed';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.pointerEvents = 'none';
    canvas.style.zIndex = '50';
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    document.body.appendChild(canvas);
    canvasRef.current = canvas;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Mouse move handler
    const handleMouseMove = (e: MouseEvent) => {
      mousePositions.current.push({
        x: e.clientX,
        y: e.clientY,
        time: Date.now()
      });

      // Keep only recent positions
      if (mousePositions.current.length > options.trailLength) {
        mousePositions.current.shift();
      }
    };

    // Animation loop
    const animate = () => {
      if (!ctx || !canvasRef.current) return;

      // Clear canvas with fade effect
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

      // Remove old positions
      const now = Date.now();
      mousePositions.current = mousePositions.current.filter(
        pos => now - pos.time < 800
      );

      // Draw soft line trail
      if (mousePositions.current.length > 1) {
        const color = options.colors[0]; // Use first color

        for (let i = 1; i < mousePositions.current.length; i++) {
          const prevPos = mousePositions.current[i - 1];
          const currPos = mousePositions.current[i];
          const age = now - currPos.time;
          const lifePercent = 1 - (age / 800);
          const alphaMultiplier = lifePercent * options.opacity;

          // Draw soft line segment
          ctx.strokeStyle = `${color}${Math.floor(alphaMultiplier * 255).toString(16).padStart(2, '0')}`;
          ctx.lineWidth = options.size / 10; // Make line thinner
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';

          ctx.beginPath();
          ctx.moveTo(prevPos.x, prevPos.y);
          ctx.lineTo(currPos.x, currPos.y);
          ctx.stroke();
        }
      }

      animationFrameId.current = requestAnimationFrame(animate);
    };

    // Event listeners
    window.addEventListener('mousemove', handleMouseMove);
    
    // Resize handler
    const handleResize = () => {
      if (canvasRef.current) {
        canvasRef.current.width = window.innerWidth;
        canvasRef.current.height = window.innerHeight;
      }
    };
    window.addEventListener('resize', handleResize);

    // Start animation
    animate();

    // Cleanup
    return () => {
      console.info('[MouseTrail] Cleaning up');
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
      if (canvasRef.current && document.body.contains(canvasRef.current)) {
        document.body.removeChild(canvasRef.current);
      }
      delete document.body.dataset.mouseTrail;
    };
  }, [options.enabled, options.colors, options.size, options.opacity, options.trailLength]);
};
